
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { ApplicationSection } from "@/components/application/ApplicationSection";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2 } from "lucide-react";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Application } from "@/types/database.types";
import type { AdditionalContact, PayrollSchedule } from "@/types/database/application-types";
import type { CorporateStructure } from "@/types/database/common-types";
import type { Json } from "@/integrations/supabase/types";
import { 
  processAdditionalContacts, 
  processPayrollSchedules, 
  normalizeCorporateStructure, 
  ensureStringArray,
  processHealthPlans
} from "@/utils/application-data-utils";

interface ApplicationViewSheetProps {
  applicationId: string | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ApplicationViewSheet = ({ 
  applicationId, 
  isOpen, 
  onOpenChange 
}: ApplicationViewSheetProps) => {
  const [application, setApplication] = useState<Partial<Application> | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [activeSection, setActiveSection] = useState<string>("0");

  useEffect(() => {
    const fetchApplication = async () => {
      if (!applicationId) return;

      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from('applications')
          .select('*')
          .eq('id', applicationId)
          .single();

        if (error) throw error;
        
        // Process the raw application data to match the Application type
        const processedData = {
          ...data,
          // Convert JSON fields to proper typed arrays
          additional_contacts: processAdditionalContacts(data.additional_contacts as Json),
          additional_schedules: processPayrollSchedules(data.additional_schedules as Json[]),
          health_plans: processHealthPlans(data.health_plans as Json),
          // Ensure string arrays 
          desired_medical_carriers: ensureStringArray(data.desired_medical_carriers),
          desired_ancillary_carriers: ensureStringArray(data.desired_ancillary_carriers),
          other_documents: ensureStringArray(data.other_documents),
          corporate_structure: normalizeCorporateStructure(data.corporate_structure),
          states_of_operation: ensureStringArray(data.states_of_operation),
          states_with_employees: ensureStringArray(data.states_with_employees),
          top_3_peo_needs: ensureStringArray(data.top_3_peo_needs),
          preferred_peos: ensureStringArray(data.preferred_peos),
          excluded_peos: ensureStringArray(data.excluded_peos),
        };
        
        setApplication(processedData as unknown as Partial<Application>);
      } catch (error) {
        console.error("Error fetching application:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (isOpen && applicationId) {
      fetchApplication();
    } else {
      setApplication(null);
    }
  }, [applicationId, isOpen]);

  // Dummy function since we're in view-only mode, but return a Promise to satisfy the type requirement
  const handleSave = async (data?: Partial<Application>) => {
    return Promise.resolve();
  };

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-xl md:max-w-2xl overflow-y-auto">
        <SheetHeader className="mb-4">
          <SheetTitle>
            {application?.company_legal_name || "Application Details"}
          </SheetTitle>
        </SheetHeader>

        {isLoading ? (
          <div className="flex items-center justify-center h-40">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : application ? (
          <Tabs defaultValue="0" value={activeSection} onValueChange={setActiveSection}>
            <TabsList className="grid grid-cols-7 mb-4">
              <TabsTrigger value="0">Contact</TabsTrigger>
              <TabsTrigger value="1">Company</TabsTrigger>
              <TabsTrigger value="2">Insurance</TabsTrigger>
              <TabsTrigger value="3">Operations</TabsTrigger>
              <TabsTrigger value="4">Health</TabsTrigger>
              <TabsTrigger value="5">Documents</TabsTrigger>
              <TabsTrigger value="6">PEO</TabsTrigger>
            </TabsList>
            
            {[
              { tabValue: "0", sectionNumber: 0 }, // Contact
              { tabValue: "1", sectionNumber: 1 }, // Company  
              { tabValue: "2", sectionNumber: 2 }, // Insurance
              { tabValue: "3", sectionNumber: 3 }, // Operations
              { tabValue: "4", sectionNumber: 4 }, // Health
              { tabValue: "5", sectionNumber: 7 }, // Documents
              { tabValue: "6", sectionNumber: 8 }, // PEO
            ].map(({ tabValue, sectionNumber }) => (
              <TabsContent key={tabValue} value={tabValue}>
                <div className="border rounded-lg p-4">
                  <ApplicationSection 
                    currentSection={sectionNumber} 
                    data={application} 
                    onSave={handleSave} 
                  />
                </div>
              </TabsContent>
            ))}
          </Tabs>
        ) : (
          <div className="text-center text-muted-foreground">
            No application data found
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
